import React, {useEffect, useMemo, useState} from 'react';
import {toast} from "react-toastify";
import {useAuth} from "../../contexts/AuthContext";
import {IconEye, IconEyeSlash} from "../../../components/icons";
import {confirmUserPassword} from "../../../components/requests";

const LoginPage = () => {
  const {login} = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [token, setToken] = useState<string|any>(null);
  const [expires, setExpires] = useState<string|any>(null);
  const [isActivateAccount, setIsActivateAccount] = useState<boolean>(false);
  const isConfirmPassword = !!((token && expires) || isActivateAccount);
  const PUBLIC_URL = process.env.PUBLIC_URL;

  // Verification password
  const passwordError = useMemo(() => {
    if (!(isConfirmPassword) || password.length === 0) return null;
    if (password.length < 8) {
      return "Le mot de passe doit contenir au moins 8 caractères.";
    }
    if (password !== confirmPassword) {
      return "Les mots de passe ne sont pas identiques.";
    }
    return null;
  }, [isConfirmPassword, password, confirmPassword]);

  // Verification confirmPassword
  const confirmPasswordError = useMemo(() => {
    if (!(isConfirmPassword) || confirmPassword.length === 0) return null;
    if (confirmPassword.length < 8) {
      return "Le mot de passe doit contenir au moins 8 caractères.";
    }
    if (password !== confirmPassword) {
      return "Les mots de passe ne sont pas identiques.";
    }
    return null;
  }, [isConfirmPassword, password, confirmPassword]);

// Utile si tu veux désactiver le bouton tant que c'est invalide
  const isNewPasswordValid = Boolean(
    isConfirmPassword &&
    password.length >= 8 &&
    confirmPassword.length >= 8 &&
    password === confirmPassword
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Simuler un délai durant la connexion
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Si isConfirmPassword (il y a un token et une date d'expiration)
      // et si il y a confirmation du mot de passe
      if (isConfirmPassword) {
        try {
          const payload = {
            'email': email,
            'confirmPassword': confirmPassword,
            'token': token,
            'expires': expires,
            'isActivateAccount': isActivateAccount
          }
          await confirmUserPassword(payload);
        } catch (confirmError: any) {
          toast.error(confirmError?.response?.data ?? 'Erreur');
          return;
        }
      }

      const result = await login(email, password);
      if (result === 'activate_account') {
        // Activate account - activate his account for his first login
        setIsActivateAccount(true);

        toast.success('Première connexion : Changez votre mot de passe')

        // Clearing the form
        setPassword('')
        setConfirmPassword('')
        return;
      }
      if (result === 'two_factor') {
        // 2FA required - redirection handled by AppRoutes based on twoFactorStep
        return;
      }
      if (result === true) {
        // Logique onSuccess déplacée dans AuthContext car currentUser est encore null ici
        // onLoginSuccess();
        toast.success('Connexion réussie');
      } else {
        let message = (typeof result === "string") ? result : 'Identifiants incorrects ou compte désactivé';
        setError(message);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  // Récuperation des données et nettoyage de l'URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenParam = params.get("token");
    const expiresParam = params.get("expires");

    if (tokenParam) setToken(tokenParam);
    if (expiresParam) setExpires(expiresParam);

    // Supprime les query params de l'URL sans recharger la page
    if (tokenParam && expiresParam) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Vérification de la date d'expiration
    const now = new Date();
    const expiresDate = new Date(expiresParam);
    if (now > expiresDate) {
      setToken(null);
      setExpires(null);
    }
  }, []);

  // const demoAccounts = [
  //   {label: 'Admin GSR', email: 'admin@groupesurplus.com', password: 'admin', color: 'text-amber-400'},
  //   {label: 'AXA Admin', email: 'gestionnaire@axa.fr', password: 'axa123', color: 'text-blue-400'},
  //   {label: 'MAIF Admin', email: 'gestionnaire@maif.fr', password: 'maif123', color: 'text-green-400'},
  // ];
  //
  // const fillDemo = (demoEmail: string, demoPassword: string) => {
  //   setEmail(demoEmail);
  //   setPassword(demoPassword);
  //   setError('');
  // };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e3a2f 50%, #14532d 100%)',
        }}
      >
        {/* Decorative Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 bg-emerald-500 rounded-full blur-3xl"/>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-500 rounded-full blur-3xl"/>
        </div>

        <div className="relative z-10 flex flex-col justify-center px-16 text-white">
          {/* Logo */}
          <div className="flex items-center gap-4 mb-12">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg"
              // style={{background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'}}
            >
              <span className="text-2xl font-bold">
                <img src={`${PUBLIC_URL}/favicon.ico`} alt={"logo"}/>
              </span>
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">GROUPE SURPLUS</h1>
              <p className="text-emerald-400 text-sm tracking-widest font-medium">RECYCLAGE</p>
            </div>
          </div>

          {/* Title */}
          <h2 className="text-5xl font-bold mb-6 leading-tight">
            Portail
            <br/>
            <span className="text-emerald-400">Assureurs</span>
          </h2>

          <p className="text-gray-300 text-lg max-w-md mb-10 leading-relaxed">
            Plateforme sécurisée de gestion des dossiers véhicules et relations partenaires.
          </p>

          {/* Demo Accounts */}
          {/*<div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 max-w-md border border-white/10">*/}
          {/*  <p className="text-sm text-emerald-300 font-medium mb-4 flex items-center gap-2">*/}
          {/*    <span className="text-lg">🔐</span> Comptes de démonstration*/}
          {/*  </p>*/}
          {/*  <div className="space-y-2">*/}
          {/*    {demoAccounts.map((account) => (*/}
          {/*      <button*/}
          {/*        key={account.email}*/}
          {/*        onClick={() => fillDemo(account.email, account.password)}*/}
          {/*        className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/10 transition-colors group"*/}
          {/*      >*/}
          {/*        <span className={`${account.color} font-medium`}>{account.label}:</span>*/}
          {/*        <span className="text-gray-300 text-sm ml-2 group-hover:text-white transition-colors">*/}
          {/*          {account.email}*/}
          {/*        </span>*/}
          {/*      </button>*/}
          {/*    ))}*/}
          {/*  </div>*/}
          {/*</div>*/}
        </div>
      </div>

      {/* Right Panel - Form */}
      <div
        className="flex-1 flex items-center justify-center p-8"
        style={{background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)'}}
      >
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              // style={{background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'}}
            >
              <span className="text-lg font-bold text-white">
                <img src={`${PUBLIC_URL}/favicon.ico`} alt={"logo"}/>
              </span>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">GROUPE SURPLUS</h1>
              <p className="text-emerald-600 text-xs tracking-widest">RECYCLAGE</p>
            </div>
          </div>

          {/* Form Card */}
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-10 shadow-xl border border-gray-100">
            {(isConfirmPassword) ? (
              <>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Activation de votre compte</h3>
                <p className="text-gray-500 mb-8">Définissez votre mot de passe</p>
              </>
            ) : (
              <>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Connexion</h3>
                <p className="text-gray-500 mb-8">Accédez à votre espace partenaire</p>
              </>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-center gap-2">
                <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Adresse email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3.5 rounded-xl border-2 border-gray-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 outline-none transition-all text-gray-900"
                  placeholder="votre@email.fr"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mot de passe
                </label>
                <div className="relative">
                  <input
                    type={`${showPassword ? "text" : "password"}`}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="on"
                    className="w-full px-4 py-3.5 rounded-xl border-2 border-gray-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 outline-none transition-all text-gray-900"
                    placeholder="••••••••"
                    required
                  />
                  <span
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-5 top-5 cursor-pointer select-none"
                  >
                    {showPassword ? <IconEye className="w-4 h-4 text-gray-400"/> : <IconEyeSlash className="w-4 h-4 text-gray-400"/>}
                  </span>
                </div>
                {passwordError && (
                  <div className="text-xs text-red-600 mt-1">
                    {passwordError}
                  </div>
                )}
              </div>

              {(isConfirmPassword) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirmation du mot de passe
                  </label>
                  <div className="relative">
                    <input
                      type={`${showConfirmPassword ? "text" : "password"}`}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="on"
                      className="w-full px-4 py-3.5 rounded-xl border-2 border-gray-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 outline-none transition-all text-gray-900"
                      placeholder="••••••••"
                      required
                    />
                    <span
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-5 top-5 cursor-pointer select-none"
                    >
                      {showConfirmPassword ? <IconEye className="w-4 h-4 text-gray-400"/> : <IconEyeSlash className="w-4 h-4 text-gray-400"/>}
                    </span>
                  </div>
                  {confirmPasswordError && (
                    <div className="text-xs text-red-600 mt-1">
                      {confirmPasswordError}
                    </div>
                  )}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading || (isConfirmPassword && !isNewPasswordValid)}
                className="w-full py-3.5 rounded-xl text-white font-semibold bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 transition-all shadow-lg shadow-emerald-500/25 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Connexion en cours...
                  </>
                ) : (
                  'Se connecter'
                )}
              </button>
            </form>

            {/* Help */}
            <p className="mt-6 text-center text-sm text-gray-500">
              Problème de connexion ?{' '}
              <a
                href="mailto:support-extranet@groupesurplus.fr?subject=Demande%20d%27assistance%20Extranet"
                className="text-emerald-600 hover:text-emerald-700 font-medium"
              >
                Contactez le support
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
